import { useEffect, useRef } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import {
	isBusinessDomain,
	isLikelyPersonalDomain,
	isPersonalDomain,
} from '@/components/signup/utils/is-personal-domain';
import { experiments } from '@/utils/experiment/manifest';
import { useExperiment } from '@/utils/experiment/use-experiment';

import {
	fireNudgeEmailChangedAnalytics,
	fireNudgeShownAnalytics,
} from './recommend-work-email-nudge-analytics';

/**
 * Encapsulates the `recommend-work-email-signup` experiment behaviour for the
 * inline signup input.
 *
 * Exposure for this experiment is fired automatically at page level (the
 * manifest sets `fireExperimentExposure: true`): every enrolled visitor who
 * lands on the try/cloud/signup page is exposed, with enrollment scoped by the
 * cohort targeting (logged-out Jira free signup). This hook therefore does not
 * fire exposure itself. Instead it:
 *
 *  - determines eligibility live, as the user types, once the full provider
 *    label is typed (so "ada@gmail" qualifies, but "ada@gmai" does not) —
 *    deliberately independent of the blur-gated `isValid` state so the nudge
 *    appears immediately rather than only after the field loses focus,
 *  - reports whether the treatment nudge should be shown, latching it "on" once
 *    eligible so it persists even if the email is later edited or cleared, and
 *  - fires the `shown` UI event once when the treatment nudge is first surfaced,
 *    so analysis can distinguish enrolled users who actually saw the nudge
 *    (started on a personal domain) from those who never did.
 *
 * @param email The current email input value (may be partially typed).
 * @returns `{ showNudge }` — true for the enrolled treatment arm from the moment
 *   the user first becomes eligible onward (it does not turn back off).
 */
export const useRecommendWorkEmailNudge = ({
	email,
}: {
	email: string;
}): { showNudge: boolean } => {
	const experiment = useExperiment(experiments.recommendWorkEmailSignup);
	const { createAnalyticsEvent } = useAnalyticsEvents();

	// Only treat the user as enrolled once Statsig has actually resolved their
	// arm. `type === 'enrolled'` alone is true as soon as the (synchronous)
	// targeting cohort is known, but the bucketed parameter value can still be
	// resolving — during that window `getExperimentValue` returns undefined and
	// the hook falls back to `defaultValue` (false) with `usingDefault: true`.
	// A comment elsewhere in the codebase puts it succinctly: "usingDefault is
	// equivalent to not being enrolled in Statsig".
	//
	// Gating on `usingDefault === false` here ensures the treatment nudge is only
	// surfaced once the user's bucket has genuinely resolved to treatment, rather
	// than flashing based on the default (control) value during the race window.
	// (Exposure itself is fired automatically at page level by useExperiment, so
	// this gate no longer concerns exposure balancing — only nudge display.)
	//
	// Narrow to a single resolved-enrolled reference so downstream reads of
	// `value` are type-safe (the hook returns a discriminated union whose
	// not-enrolled / indeterminate arms lack those fields).
	const resolvedExperiment =
		experiment.type === 'enrolled' && experiment.usingDefault === false ? experiment : undefined;

	// Eligible = enrollment is fully resolved AND the domain typed so far is a
	// known personal provider. Evaluated on every keystroke — no dependency on
	// blur/validation. This gates whether the nudge is shown, not exposure.
	const isEligible = resolvedExperiment !== undefined && isLikelyPersonalDomain(email);

	// Show the nudge for the enrolled treatment arm once eligible — and then keep
	// it shown. The nudge intentionally "latches": once the user's email is
	// confidently personal we surface the recommendation and leave it in place
	// even if they subsequently edit or clear the field, so it doesn't flicker
	// away mid-correction.
	const hasShownNudgeRef = useRef(false);
	if (isEligible && resolvedExperiment !== undefined && resolvedExperiment.value === true) {
		hasShownNudgeRef.current = true;
	}
	const showNudge = hasShownNudgeRef.current;

	// Fire the `recommendWorkEmailNudge shown` UI event exactly once, the first
	// time the treatment nudge is surfaced. Because exposure now fires
	// automatically at page level for every enrolled visitor, this event is what
	// lets analysis separate enrolled users who actually saw the nudge (they
	// started on a personal domain) from those who never did. It is naturally
	// scoped to personal-domain treatment users because `showNudge` only latches
	// on for the resolved treatment arm once the email is a confident personal
	// domain — the nudge-display logic above is unchanged.
	const hasFiredShownRef = useRef(false);
	useEffect(() => {
		if (showNudge && !hasFiredShownRef.current) {
			hasFiredShownRef.current = true;
			fireNudgeShownAnalytics({ createAnalyticsEvent });
		}
	}, [showNudge, createAnalyticsEvent]);

	// Fire the `recommendWorkEmailNudge emailChanged` track event exactly once,
	// when a nudged user, having entered a personal email at some point this
	// session, subsequently lands on a business email. This is the direct
	// "converted to a work email after the nudge" signal.
	//
	// This must reflect a genuine personal -> business *change of intent*, not
	// merely the current value being a business domain. The original
	// implementation gated only on `showNudge` (which latches on and never turns
	// off) plus the current value being a business domain. That mis-fired on a
	// user's *first* email whenever the field was seeded with a personal address
	// (e.g. a prefilled / type-ahead email carried over from an earlier step): the
	// nudge latched on at mount, and the user's very first edit to their real
	// (business) email immediately fired the event — even though, from the user's
	// perspective, they were still entering their first email and had not switched
	// away from anything they typed.
	//
	// To fix this we remember whether the user has *ever* held a complete, valid
	// personal email while mounted, and fire once they then reach a business
	// email. We deliberately track "has ever been personal" as a sticky latch
	// rather than only comparing against the immediately-previous render, so the
	// switch is still counted even when the user takes an indirect path — e.g.
	// personal email -> clears the field -> types a business email. From the user's
	// perspective they still moved off the personal address they had entered.
	//
	// Crucially, `hasEverBeenPersonalRef` starts as `false` and is only ever set
	// to `true` *after* the effect has run for a render — i.e. once we have
	// actually observed the user holding a valid personal email while mounted. It
	// deliberately does NOT trust the initial/seeded prop value (which may be a
	// prefilled type-ahead email), so the first render can never, by itself,
	// establish the "was personal" baseline. Combined with the one-shot
	// `hasFiredEmailChangedRef`, this means the event fires at most once, only
	// after a genuine in-session personal -> business change, and never merely
	// because the field happens to currently hold a business email. Still gated on
	// `showNudge` so it only counts users who were actually nudged.
	const hasEverBeenPersonalRef = useRef(false);
	const hasFiredEmailChangedRef = useRef(false);
	useEffect(() => {
		const isNowBusiness = isBusinessDomain(email);
		const switchedFromPersonalToBusiness = hasEverBeenPersonalRef.current && isNowBusiness;

		if (showNudge && !hasFiredEmailChangedRef.current && switchedFromPersonalToBusiness) {
			hasFiredEmailChangedRef.current = true;
			fireNudgeEmailChangedAnalytics({ createAnalyticsEvent });
		}

		// Latch "has ever been personal" once we observe a *valid, complete*
		// personal address. We only ever set it (never clear it) so that an
		// intervening cleared/partial value does not erase the fact that the user
		// had entered a personal email — that history is what makes a later
		// business email a genuine switch. Requiring a complete personal address
		// also stops partially-typed input between keystrokes from spuriously
		// establishing the baseline.
		if (isPersonalDomain(email)) {
			hasEverBeenPersonalRef.current = true;
		}
	}, [showNudge, email, createAnalyticsEvent]);

	return { showNudge };
};
